/**
 * Presentationsformatering för uppgifter från Skolverkets register.
 *
 * Registret levererar t.ex. huvudmannanamn i VERSALER och telefonnummer utan
 * gruppering. Här normaliseras texten för visning — datat i databasen rörs
 * aldrig, och vi lägger aldrig till uppgifter som inte finns.
 */

/** Ord som ska förbli gemena mitt i ett namn. */
const SMALL_WORDS = new Set(["i", "och", "för", "av", "på", "med", "till", "de", "den", "det"]);
/** Ord/förkortningar som alltid skrivs med versaler. */
const UPPER_WORDS = new Set(["AB", "HB", "KB", "AB)", "IF", "IB", "AS", "BTH", "KTH", "SLU", "SU"]);

function titleCaseToken(token: string): string {
  if (!token) return token;
  const bare = token.replace(/[^\p{L}]/gu, "");
  if (UPPER_WORDS.has(bare.toUpperCase()) && bare.length <= 3) {
    return token.toUpperCase();
  }
  // Bevara bindestreck och snedstreck: "vasa-nord" → "Vasa-Nord"
  return token
    .toLowerCase()
    .replace(/(^|[-/'’])(\p{L})/gu, (_m, sep: string, ch: string) => sep + ch.toUpperCase());
}

/**
 * Snyggar till ett huvudmannanamn som ofta kommer i VERSALER från registret.
 * Namn som redan har blandad skiftlägesform lämnas orörda.
 */
export function formatHuvudman(name: string | null | undefined): string | null {
  const raw = (name ?? "").trim();
  if (!raw) return null;
  // Innehåller gemener redan → skolan/registret har en medveten skrivning.
  if (/\p{Ll}/u.test(raw)) return raw;

  const words = raw.split(/\s+/);
  return words
    .map((w, i) => {
      const bare = w.replace(/[^\p{L}]/gu, "");
      if (i > 0 && SMALL_WORDS.has(bare.toLowerCase())) return w.toLowerCase();
      return titleCaseToken(w);
    })
    .join(" ");
}

/** Samma som ovan men med "Uppgift saknas" som fallback. */
export function huvudmanOrMissing(name: string | null | undefined): string {
  return formatHuvudman(name) ?? "Uppgift saknas";
}

/**
 * Grupperar svenska telefonnummer för läsbarhet.
 * "081234567" → "08-123 45 67", "+46812345 67" → "+46 8 123 45 67".
 */
export function formatPhone(phone: string | null | undefined): string | null {
  const raw = (phone ?? "").trim();
  if (!raw) return null;

  const intl = raw.startsWith("+46") || raw.startsWith("0046");
  const digits = raw.replace(/\D/g, "").replace(/^0046/, "").replace(/^46/, intl ? "" : "46");
  const national = intl ? `0${digits}` : raw.replace(/\D/g, "");
  if (national.length < 7 || national.length > 11) return raw;

  // Riktnummerlängd: 08 (Stockholm) = 2, 03x/04x/05x… = 3, mobil 07x = 3.
  const areaLen = national.startsWith("08") ? 2 : 3;
  const area = national.slice(0, areaLen);
  const rest = national.slice(areaLen);

  // Gruppera resten i 3+2+2 / 2+2+2 beroende på längd.
  const groups: string[] = [];
  let i = 0;
  if (rest.length % 2 === 1) {
    groups.push(rest.slice(0, 3));
    i = 3;
  }
  for (; i < rest.length; i += 2) groups.push(rest.slice(i, i + 2));

  const formatted = `${area}-${groups.join(" ")}`.trim();
  return intl ? `+46 ${formatted.slice(1)}` : formatted;
}

/** tel:-länk i E.164-format där det går. */
export function telHref(phone: string | null | undefined): string | null {
  const raw = (phone ?? "").trim();
  if (!raw) return null;
  const digits = raw.replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) return `tel:${digits}`;
  if (digits.startsWith("0046")) return `tel:+${digits.slice(2)}`;
  if (digits.startsWith("0")) return `tel:+46${digits.slice(1)}`;
  return `tel:${digits}`;
}

const GRADE_ORDER = ["F", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

/**
 * Komprimerar en årskurslista till intervall: ["F","1","2","3"] → "F–3".
 * Icke sammanhängande listor visas som "F–3, 7–9".
 */
export function formatGradeYears(years: string[] | null | undefined): string | null {
  const clean = (years ?? [])
    .map((y) => y.trim().replace(/^(årskurs|åk)\s*/i, "").toUpperCase())
    .filter(Boolean);
  if (clean.length === 0) return null;

  const known = clean.filter((y) => GRADE_ORDER.includes(y));
  const unknown = clean.filter((y) => !GRADE_ORDER.includes(y));
  if (known.length === 0) return clean.join(", ");

  const idx = [...new Set(known.map((y) => GRADE_ORDER.indexOf(y)))].sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = idx[0]!;
  let prev = start;
  for (const cur of idx.slice(1)) {
    if (cur === prev + 1) {
      prev = cur;
      continue;
    }
    ranges.push(start === prev ? GRADE_ORDER[start]! : `${GRADE_ORDER[start]}–${GRADE_ORDER[prev]}`);
    start = cur;
    prev = cur;
  }
  ranges.push(start === prev ? GRADE_ORDER[start]! : `${GRADE_ORDER[start]}–${GRADE_ORDER[prev]}`);

  return [...ranges, ...unknown].join(", ");
}

/**
 * Ort + kommun utan dubblering: ("Danderyd", "Danderyd") → "Danderyd".
 * Kommunnamn med suffixet "s kommun" jämförs på grundformen.
 */
export function formatOrt(city: string | null | undefined, municipality: string | null | undefined): string | null {
  const norm = (v: string | null | undefined) =>
    (v ?? "")
      .trim()
      .replace(/\s+kommun$/i, "")
      .trim();

  const c = norm(city);
  const m = norm(municipality);
  if (!c && !m) return null;
  if (!c) return m;
  if (!m) return c;
  if (c.toLocaleLowerCase("sv") === m.toLocaleLowerCase("sv")) return c;
  return `${c}, ${m} kommun`;
}

/**
 * Gatuadress ur registret i normal skrivning.
 *
 * Skolverket och Stockholms öppna data levererar ofta adressen i VERSALER
 * ("SETTERGATAN 5"). Vi ändrar aldrig datat — bara hur det skrivs ut. Adresser
 * med blandad skiftlägesform lämnas orörda, för då är skrivningen medveten.
 * Lägenhets- och uppgångsbeteckningar (A, B, LGH) behåller versal.
 */
export function formatAddress(address: string | null | undefined): string | null {
  const raw = (address ?? "").trim().replace(/\s+/g, " ");
  if (!raw) return null;
  if (/\p{Ll}/u.test(raw)) return raw;

  return raw
    .split(" ")
    .map((word) => {
      const bare = word.replace(/[^\p{L}\p{N}]/gu, "");
      // Rena siffror och korta beteckningar ("5", "5A", "B", "LGH") lämnas.
      if (/^\d/.test(bare)) return word.toUpperCase();
      if (bare.length <= 1) return word.toUpperCase();
      if (/^(LGH|BV|NB|PL|C\/O)$/i.test(bare)) return word.toUpperCase();
      return titleCaseToken(word);
    })
    .join(" ");
}

/** Full adressrad: "Settergatan 5, 681 30 Kristinehamn". Tomma delar utelämnas. */
export function formatFullAddress(
  street: string | null | undefined,
  postalCode: string | null | undefined,
  city: string | null | undefined,
): string | null {
  const gata = formatAddress(street);
  const ort = formatAddress(city);
  const post = [(postalCode ?? "").trim(), ort].filter(Boolean).join(" ");
  const parts = [gata, post].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}
